import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { StarRating } from "@/components/StarRating";
import FavoriteBusinessButton from "@/components/FavoriteBusinessButton";
import FriendsWhoFavorited from "@/components/FriendsWhoFavorited";
import { useTurnstile, TurnstileMount } from "@/components/TurnstileWidget";
import { DisputeBlock } from "@/components/DisputeBlock";
import VerifiedBadge from "@/components/VerifiedBadge";
import ProBadge from "@/components/ProBadge";
import FoundingMemberBadge from "@/components/FoundingMemberBadge";
import { Share2, Copy, Check, MapPin, Megaphone, Tag, BellOff, Bell, MessageSquareQuote, Sparkles, ShieldCheck, ExternalLink } from "lucide-react";
import SocialLinks from "@/components/SocialLinks";
import ReferAFriendDialog from "@/components/ReferAFriendDialog";
import CheckInDialog from "@/components/CheckInDialog";
import CustomerPhotos from "@/components/CustomerPhotos";
import BusinessCheckInPhotos from "@/components/BusinessCheckInPhotos";
import BusinessEmbedWidgetCard from "@/components/BusinessEmbedWidgetCard";
import HoursDisplay from "@/components/HoursDisplay";
import OpenTableWidget from "@/components/OpenTableWidget";
import BusinessMap from "@/components/BusinessMap";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";

export default function BusinessDetail() {
  const { id } = useParams();
  const { user, refresh } = useAuth();
  const [biz, setBiz] = useState(null);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [responding, setResponding] = useState(null); // review id
  const [responseText, setResponseText] = useState("");
  const [messaging, setMessaging] = useState(null); // review id
  const [messageText, setMessageText] = useState("");
  const [messageCode, setMessageCode] = useState("");
  const [reviewHoneypot, setReviewHoneypot] = useState("");
  const reviewTurnstile = useTurnstile();
  const [sendingMsg, setSendingMsg] = useState(false);
  const [copied, setCopied] = useState(false);
  const [refProgram, setRefProgram] = useState(null);

  const load = async () => {
    const { data } = await api.get(`/businesses/${id}`);
    setBiz(data);
  };

  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    api.get(`/businesses/${id}/referral-program`)
      .then(({ data }) => { if (!cancelled) setRefProgram(data); })
      .catch(() => { if (!cancelled) setRefProgram(null); });
    return () => { cancelled = true; };
  }, [id]);

  const sharePage = async () => {
    const url = window.location.href;
    const data = {
      title: `${biz?.name || "Business"} on Servrevu`,
      text: `Leave a review for ${biz?.name || "this business"} on Servrevu`,
      url,
    };
    if (navigator.share) {
      try { await navigator.share(data); return; } catch (e) { console.warn("navigator.share failed, falling back to clipboard:", e); }
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.error("Clipboard copy failed:", e);
      toast.error("Couldn't copy link — please copy from the address bar.");
    }
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (!rating) return toast.error("Please choose a rating");
    setSubmitting(true);
    try {
      await api.post(`/businesses/${id}/reviews`, {
        rating, comment,
        turnstile_token: reviewTurnstile.token,
        _hp_website: reviewHoneypot,
      });
      toast.success("Review posted");
      setRating(0);
      setComment("");
      setReviewHoneypot("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
      reviewTurnstile.reset();
    } finally {
      setSubmitting(false);
    }
  };

  const isMuted = Array.isArray(user?.unsubscribed_from) && user.unsubscribed_from.includes(id);
  const [muteBusy, setMuteBusy] = useState(false);
  const toggleMute = async () => {
    setMuteBusy(true);
    try {
      if (isMuted) {
        await api.delete(`/me/unsubscribes/${id}`);
        toast.success(`Resubscribed to offers from ${biz?.name || "this business"}`);
      } else {
        await api.post(`/me/unsubscribes/${id}`);
        toast.success(`Muted promotional emails from ${biz?.name || "this business"}`);
      }
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setMuteBusy(false);
    }
  };

  if (!biz) return <div className="max-w-5xl mx-auto px-6 py-12 text-gray-500">Loading...</div>;

  // Rating-coded color tone for review cards & rating chip
  const ratingTone = (r) => {
    if (r >= 4.5) return { chip: "bg-emerald-100 text-emerald-800 border-emerald-300", accent: "border-l-emerald-500" };
    if (r >= 3.5) return { chip: "bg-sky-100 text-sky-800 border-sky-300", accent: "border-l-sky-500" };
    if (r >= 2.5) return { chip: "bg-amber-100 text-amber-800 border-amber-300", accent: "border-l-amber-500" };
    if (r > 0)    return { chip: "bg-rose-100 text-rose-800 border-rose-300", accent: "border-l-rose-500" };
    return { chip: "bg-gray-100 text-gray-700 border-gray-300", accent: "border-l-gray-300" };
  };
  const heroTone = ratingTone(biz.rating_avg || 0);

  return (
    <div className="min-h-screen bg-white">
    <div className="max-w-5xl mx-auto px-6 md:px-12 py-12">
      {/* HERO */}
      <div className="relative overflow-hidden rounded-2xl border border-border bg-white shadow-sm" data-testid="business-hero">
        <div className="relative pt-8 pb-8 px-6 md:px-10">
        <div className="flex items-start gap-5 flex-wrap">
          {biz.logo_data_url ? (
            <img
              src={biz.logo_data_url}
              alt={`${biz.name} logo`}
              className="h-24 w-24 shrink-0 rounded-2xl border border-border object-contain bg-white shadow-md"
              data-testid="business-logo"
            />
          ) : (
            <div className="h-24 w-24 shrink-0 rounded-2xl border border-border bg-white shadow-md flex items-center justify-center">
              <span className="font-display text-3xl text-[#B31E1E]">{(biz.name || "?").trim()[0]?.toUpperCase()}</span>
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h1 className="font-display text-3xl sm:text-4xl md:text-5xl tracking-tighter flex flex-wrap items-center gap-x-2 gap-y-1" data-testid="business-name">
              <span
                className={(biz.name || "").length > 18 ? "max-w-[18ch] sm:max-w-[22ch] md:max-w-[26ch]" : ""}
              >
                {biz.name}
              </span>
              {biz.is_pro_subscriber && <ProBadge size={22} showLabel />}
              {biz.is_verified && <VerifiedBadge size={26} showLabel />}
              {biz.is_founding_member && <FoundingMemberBadge size={16} showLabel />}
              {biz.claimed === false && (
                <Badge variant="outline" className="rounded-sm text-[10px] font-mono border-amber-400 text-amber-700" data-testid="business-unclaimed-badge">Unclaimed</Badge>
              )}
            </h1>
            {(biz.address_full || biz.address) && (
              <p className="mt-2 text-gray-600 flex flex-wrap items-center gap-x-2 gap-y-1 break-words" data-testid="business-address-full">
                <MapPin size={14} className="text-gray-400" />
                <span className="break-words">{biz.address_full || biz.address}</span>
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                    `${biz.name || ""} ${biz.address_full || biz.address}`
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-[#B31E1E] hover:underline font-medium whitespace-nowrap"
                  data-testid="business-map-link"
                >
                  View on map →
                </a>
              </p>
            )}
            {biz.date_established && (() => {
              const raw = String(biz.date_established);
              const yr = parseInt(raw.slice(0, 4), 10);
              if (isNaN(yr)) return null;
              const years = new Date().getFullYear() - yr;
              return (
                <p className="mt-1 text-xs font-mono text-gray-500" data-testid="business-date-established">
                  Established {yr}
                  {years > 0 && (
                    <span className="ml-2 text-[#B31E1E] font-semibold">
                      · {years} {years === 1 ? "year" : "years"} in business
                    </span>
                  )}
                </p>
              );
            })()}
            {/* Rating + share — pill row */}
            <div className="mt-4 flex items-center gap-2 flex-wrap">
              <div className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 ${heroTone.chip}`}>
                <StarRating value={biz.rating_avg} size={16} />
                <span className="text-sm font-semibold tabular-nums">{(biz.rating_avg || 0).toFixed(1)}</span>
              </div>
              <div className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs font-mono text-gray-600">
                <MessageSquareQuote size={12} /> {biz.rating_count} review{biz.rating_count === 1 ? "" : "s"}
              </div>
              {biz.rating_avg >= 4.5 && biz.rating_count >= 5 && (
                <Badge className="rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white border-0 px-3 py-1.5" data-testid="business-top-rated-badge">
                  <Sparkles size={12} className="mr-1" /> Top rated
                </Badge>
              )}
              <Button
                onClick={sharePage}
                variant="outline"
                size="sm"
                className="rounded-[35px] shadow-sm hover:shadow-md ml-auto"
                data-testid="business-share-button"
              >
                {copied ? <Check size={14} className="mr-1" /> : <Share2 size={14} className="mr-1" />}
                {copied ? "Copied" : "Share"}
              </Button>
              <FavoriteBusinessButton
                businessId={id}
                initialCount={biz.follower_count || 0}
              />
            </div>
            <FriendsWhoFavorited businessId={id} />
          </div>
        </div>
        {biz.description && <p className="mt-6 max-w-2xl leading-relaxed text-gray-700">{biz.description}</p>}

        <SocialLinks socials={biz.socials} size={18} className="mt-6" />

        {user?.role === "customer" && refProgram?.active && (
          <div className="mt-6" data-testid="refer-a-friend-slot">
            <ReferAFriendDialog
              businessId={biz.id}
              businessName={biz.name}
              programActive={!!refProgram?.active}
            />
          </div>
        )}

        {user?.role === "customer" && biz.loyalty?.enabled && (
          <div className="mt-4" data-testid="checkin-slot">
            <CheckInDialog business={biz} onCheckedIn={load} />
            {biz.loyalty.visits_required && (
              <p className="text-[11px] font-mono text-gray-500 mt-2">
                Loyalty stamp card: fill {biz.loyalty.visits_required} visits to earn a reward.
              </p>
            )}
          </div>
        )}

        {user?.role === "customer" && (
          <div className="mt-4" data-testid="mute-business-slot">
            <Button
              variant="outline"
              size="sm"
              onClick={toggleMute}
              disabled={muteBusy}
              className="rounded-[35px] text-gray-700"
              data-testid="mute-business-button"
            >
              {isMuted ? (
                <><Bell size={13} className="mr-1.5" /> Resubscribe to offers</>
              ) : (
                <><BellOff size={13} className="mr-1.5" /> Mute promotional emails</>
              )}
            </Button>
            <p className="text-[11px] font-mono text-gray-500 mt-1.5">
              {isMuted
                ? `You won't receive promo emails from ${biz.name}.`
                : `Stop receiving promo emails from ${biz.name} only. Other businesses are unaffected.`}
            </p>
          </div>
        )}

        {(biz.phone || biz.website || biz.contact_email) && (
          <dl className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-2xl" data-testid="business-contact-block">
            {biz.phone && (
              <div>
                <dt className="overline text-gray-500">Phone</dt>
                <dd className="text-sm mt-1"><a href={`tel:${biz.phone}`} className="text-[#B31E1E] hover:underline" data-testid="business-phone">{biz.phone}</a></dd>
              </div>
            )}
            {biz.website && (
              <div>
                <dt className="overline text-gray-500">Website</dt>
                <dd className="text-sm mt-1 break-all"><a href={biz.website.startsWith("http") ? biz.website : `https://${biz.website}`} target="_blank" rel="noopener noreferrer" className="text-[#B31E1E] hover:underline" data-testid="business-website">{biz.website.replace(/^https?:\/\//, "")}</a></dd>
              </div>
            )}
            {biz.contact_email && (
              <div>
                <dt className="overline text-gray-500">Email</dt>
                <dd className="text-sm mt-1 break-all"><a href={`mailto:${biz.contact_email}`} className="text-[#B31E1E] hover:underline" data-testid="business-contact-email">{biz.contact_email}</a></dd>
              </div>
            )}
          </dl>
        )}
        {biz.license_number && (
          <div className="mt-6 inline-flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50/70 px-4 py-3 max-w-2xl" data-testid="business-license-badge">
            <div className="mt-0.5 rounded-lg bg-emerald-100 p-1.5 text-emerald-700">
              <ShieldCheck size={16} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[11px] font-mono uppercase tracking-wider text-emerald-800">
                Licensed{biz.license_state ? ` in ${biz.license_state}` : ""}
              </div>
              <div className="mt-0.5 text-sm text-[#1B1614]">
                <span className="font-mono font-semibold" data-testid="business-license-number">
                  {biz.license_type ? `${biz.license_type} ` : ""}#{biz.license_number}
                </span>
                {biz.license_verification_url && (
                  <a
                    href={biz.license_verification_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ml-3 inline-flex items-center gap-1 text-[12px] text-emerald-800 hover:text-emerald-900 hover:underline"
                    data-testid="business-license-verify-link"
                  >
                    Verify <ExternalLink size={11} />
                  </a>
                )}
              </div>
            </div>
          </div>
        )}
        {biz.claimed === false && (
          <div className="mt-6 p-4 border border-amber-300 bg-amber-50 rounded-sm">
            <p className="text-sm text-amber-900">Is this your business?{" "}
              <Link to="/register" className="font-medium underline" data-testid="claim-listing-link">Claim this listing</Link>{" "}
              with the email it was submitted under to manage it.</p>
          </div>
        )}
        </div>{/* /relative inner padding */}
      </div>{/* /hero card */}

      <CustomerPhotos businessId={biz.id} businessName={biz.name} />

      {/* Website embed widget — only the owner sees this on their public profile */}
      {user?.role === "business" && user.id === biz.id && (
        <div className="mt-8" data-testid="embed-widget-owner-section">
          <BusinessEmbedWidgetCard businessId={biz.id} />
        </div>
      )}

      <BusinessMap
        businessId={biz.id}
        businessName={biz.name}
        address={biz.address_full || biz.address}
      />

      <HoursDisplay hours={biz.hours} />

      {biz.opentable_rid && (
        <OpenTableWidget rid={biz.opentable_rid} businessName={biz.name} />
      )}


      {biz.offers && biz.offers.length > 0 && (
        <div className="mt-10">
          <h3 className="overline text-gray-500 inline-flex items-center gap-1.5">
            <Megaphone size={12} /> Current offers
          </h3>
          <ul className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="business-offers">
            {biz.offers.map((p) => (
              <li
                key={p.id}
                className="rounded-2xl border border-fuchsia-200 bg-gradient-to-br from-fuchsia-50 via-white to-indigo-50/50 p-5 shadow-sm hover:shadow-md transition-shadow"
                data-testid={`business-offer-${p.id}`}
              >
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-fuchsia-500 text-white shadow-sm">
                    <Megaphone size={13} />
                  </span>
                  <span className="overline text-fuchsia-700">Limited offer</span>
                </div>
                <h4 className="font-display text-xl tracking-tight mt-2">{p.title}</h4>
                <p className="mt-2 text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{p.description}</p>
                <div className="mt-4 flex items-center justify-between flex-wrap gap-2">
                  {p.discount_code ? (
                    <span className="inline-flex items-center gap-1 font-mono text-[12px] tracking-widest bg-white text-fuchsia-700 border border-dashed border-fuchsia-400 rounded-md px-3 py-1.5 shadow-sm">
                      <Tag size={11} /> {p.discount_code}
                    </span>
                  ) : <span />}
                  <span className="text-[11px] font-mono text-gray-500">Expires {p.expires_at}</span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {biz.gallery && biz.gallery.length > 0 && (
        <div className="mt-10">
          <h3 className="overline text-gray-500">Examples of our work</h3>
          <ul className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="business-gallery">
            {biz.gallery.map((img) => (
              <li
                key={img.id}
                className="group relative aspect-square overflow-hidden rounded-[35px] border border-border bg-gray-50"
                data-testid={`gallery-public-${img.id}`}
              >
                <a href={img.data_url} target="_blank" rel="noopener noreferrer" className="block h-full w-full">
                  <img
                    src={img.data_url}
                    alt={img.caption || `${biz.name} work sample`}
                    className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                    loading="lazy"
                  />
                  {img.caption && (
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 to-transparent p-2">
                      <p className="text-[11px] text-white truncate">{img.caption}</p>
                    </div>
                  )}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      {user?.role === "customer" && (
        <Card className="rounded-2xl border border-border mt-8 p-6 shadow-sm bg-white" data-testid="business-review-form-card">
          <div className="flex items-center gap-2">
            <div className="h-8 w-1 rounded bg-[#B31E1E]" aria-hidden="true" />
            <h3 className="font-display text-2xl">Write a review</h3>
          </div>
          <form className="mt-4 space-y-4" onSubmit={submitReview}>
            <StarRating value={rating} onChange={setRating} readOnly={false} size={24} testId="review-rating" />
            <Textarea
              rows={4}
              required
              placeholder="Share what your experience was like..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="rounded-md"
              data-testid="review-comment-input"
            />
            <input
              type="text"
              name="_hp_website"
              value={reviewHoneypot}
              onChange={(e) => setReviewHoneypot(e.target.value)}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              style={{ position: "absolute", left: "-10000px", width: 1, height: 1, opacity: 0 }}
              data-testid="review-honeypot"
            />
            <TurnstileMount widgetRef={reviewTurnstile.widgetRef} sitekey={reviewTurnstile.sitekey} />
            <Button type="submit" disabled={submitting} className="rounded-[35px] shadow-sm hover:shadow-md bg-[#B31E1E] hover:bg-[#8F1717]" data-testid="review-submit-button">
              {submitting ? "Posting..." : "Post review"}
            </Button>
          </form>
        </Card>
      )}

      <div className="mt-10">
        <h3 className="overline text-gray-500 inline-flex items-center gap-1.5">
          <MessageSquareQuote size={12} /> Customer reviews
        </h3>
        <div className="mt-4 space-y-4" data-testid="business-reviews">
          {biz.reviews.length === 0 && <p className="text-gray-500 text-sm italic">No reviews yet. Be the first.</p>}
          {biz.reviews.map((r, idx) => {
            const t = ratingTone(r.rating);
            return (
            <Card key={r.id} className={`rounded-2xl border border-border border-l-4 ${t.accent} p-5 bg-white shadow-sm hover:shadow-md transition-shadow fade-up-${(idx % 4) + 1}`} data-testid={`review-${r.id}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className="font-medium text-gray-900">{r.author_name}</span>
                <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 ${t.chip}`}>
                  <StarRating value={r.rating} size={12} />
                  <span className="text-xs font-semibold tabular-nums">{r.rating}.0</span>
                </span>
              </div>
              <p className="mt-2.5 text-sm text-gray-800 leading-relaxed">{r.comment}</p>
              <p className="mt-3 text-[11px] font-mono text-gray-400">{new Date(r.created_at).toLocaleDateString()}</p>

              {r.response && (
                <div className="mt-3 ml-4 pl-3 border-l-2 border-[#B31E1E]/30">
                  <p className="text-xs font-mono text-[#B31E1E]">Response from {r.response_author}</p>
                  <p className="text-sm text-gray-700 mt-1">{r.response}</p>
                </div>
              )}

              {user?.role === "business" && user.id === biz.id && !r.response && (
                <div className="mt-3">
                  {responding === r.id ? (
                    <div className="space-y-2">
                      <Textarea rows={3} value={responseText} onChange={(e) => setResponseText(e.target.value)} placeholder="Respond as the business owner..." className="rounded-sm" data-testid={`response-input-${r.id}`} />
                      <div className="flex gap-2">
                        <Button size="sm" className="rounded-[35px] shadow-sm hover:shadow-md bg-[#B31E1E] hover:bg-[#8F1717]" data-testid={`response-submit-${r.id}`}
                          onClick={async () => {
                            try {
                              await api.post(`/reviews/${r.id}/response`, { response: responseText });
                              toast.success("Response posted");
                              setResponding(null); setResponseText("");
                              load();
                            } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
                          }}>Post response</Button>
                        <Button variant="ghost" size="sm" className="rounded-[35px] shadow-sm hover:shadow-md" onClick={() => { setResponding(null); setResponseText(""); }}>Cancel</Button>
                      </div>
                    </div>
                  ) : (
                    <Button variant="outline" size="sm" className="rounded-[35px] shadow-sm hover:shadow-md" onClick={() => setResponding(r.id)} data-testid={`response-open-${r.id}`}>
                      Respond as owner
                    </Button>
                  )}
                </div>
              )}

              {user?.role === "business" && user.id === biz.id && (
                <div className="mt-3">
                  {messaging === r.id ? (
                    <div className="space-y-2 border border-border rounded-md p-3 bg-gray-50/50">
                      <p className="overline text-gray-500">Send a private message to this reviewer</p>
                      <Textarea
                        rows={3}
                        value={messageText}
                        onChange={(e) => setMessageText(e.target.value)}
                        placeholder="Thanks for the review! Here's 15% off your next visit…"
                        className="rounded-md"
                        data-testid={`message-text-${r.id}`}
                      />
                      <Input
                        value={messageCode}
                        onChange={(e) => setMessageCode(e.target.value)}
                        placeholder="Optional discount code (e.g. THANKYOU15)"
                        maxLength={40}
                        className="rounded-md"
                        data-testid={`message-code-${r.id}`}
                      />
                      <div className="flex gap-2 items-center flex-wrap">
                        <Button
                          size="sm"
                          disabled={sendingMsg || !user?.subscription_active}
                          className="rounded-[35px] shadow-sm hover:shadow-md bg-[#B31E1E] hover:bg-[#8F1717]"
                          data-testid={`message-send-${r.id}`}
                          onClick={async () => {
                            if (messageText.trim().length < 10) {
                              toast.error("Message must be at least 10 characters");
                              return;
                            }
                            setSendingMsg(true);
                            try {
                              const { data } = await api.post(`/reviews/${r.id}/contact-author`, { message: messageText, discount_code: messageCode });
                              if (data.sent) {
                                toast.success("Message sent — we delivered it on your behalf");
                              } else {
                                toast.message("Message logged", { description: data.skipped_reason || "Email could not be delivered." });
                              }
                              setMessaging(null); setMessageText(""); setMessageCode("");
                            } catch (err) {
                              toast.error(formatApiError(err.response?.data?.detail) || err.message);
                            } finally {
                              setSendingMsg(false);
                            }
                          }}
                        >
                          {sendingMsg ? "Sending…" : "Send message"}
                        </Button>
                        <Button variant="ghost" size="sm" className="rounded-[35px]" onClick={() => { setMessaging(null); setMessageText(""); setMessageCode(""); }}>
                          Cancel
                        </Button>
                      </div>
                      <p className="text-[11px] text-gray-500">
                        We email this on your behalf. The reviewer's address is never shared with you. Up to 3 messages per review.
                      </p>
                      {!user?.subscription_active && (
                        <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5">
                          An active servrevu Pro subscription is required to send messages.
                        </p>
                      )}
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-[35px] shadow-sm hover:shadow-md"
                      onClick={() => setMessaging(r.id)}
                      data-testid={`message-open-${r.id}`}
                    >
                      Send message / offer to reviewer
                    </Button>
                  )}
                </div>
              )}

              <DisputeBlock review={r} currentUser={user} onUpdated={load} />
            </Card>
            );
          })}
        </div>

        <BusinessCheckInPhotos businessId={id} />
      </div>
    </div>
    </div>
  );
}
